import { format, formatDistanceToNow } from 'date-fns'
import { enUS, nl } from 'date-fns/locale'
import { Calendar, User, GitCompare } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/base/buttons'
import type { Revision } from '@/types/revision'

interface RevisionItemProps {
  /** The revision to display */
  revision: Revision
  /** Whether this is the first (most recent) revision */
  isFirst?: boolean
  /** Whether this is the last (oldest) revision */
  isLast?: boolean
  /** Author display name */
  authorName?: string
  /** Callback when compare button is clicked */
  onCompare: (revision: Revision) => void
}

/**
 * Single revision item in the timeline list
 */
export function RevisionItem({
  revision,
  isFirst = false,
  isLast = false,
  authorName,
  onCompare,
}: RevisionItemProps) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language === 'nl' ? nl : enUS
  const date = new Date(revision.date)
  const formattedDate = format(date, 'PPp', { locale })
  const relativeTime = formatDistanceToNow(date, { addSuffix: true, locale })

  return (
    <div className="relative flex gap-4">
      {/* Timeline line */}
      <div className="flex flex-col items-center">
        {/* Top line (not for first item) */}
        {!isFirst && (
          <div className="h-4 w-0.5 bg-gray-300 dark:bg-gray-600" />
        )}

        {/* Dot */}
        <div
          className={`relative z-10 flex h-4 w-4 items-center justify-center rounded-full border-2 ${
            isFirst
              ? 'border-primary-600 bg-primary-100 dark:border-primary-400 dark:bg-primary-900/30'
              : 'border-gray-300 bg-white dark:border-gray-600 dark:bg-gray-800'
          }`}
        >
          <div
            className={`h-1.5 w-1.5 rounded-full ${
              isFirst ? 'bg-primary-600 dark:bg-primary-400' : 'bg-gray-400 dark:bg-gray-500'
            }`}
          />
        </div>

        {/* Bottom line (not for last item) */}
        {!isLast && (
          <div className="w-0.5 flex-1 bg-gray-300 dark:bg-gray-600" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 pb-6">
        <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          {/* Header with message */}
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <p className="font-medium text-gray-900 dark:text-gray-100">
                {revision.message || t('resources.assistants.revisions.configuration_updated')}
              </p>
              {revision.description && (
                <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                  {revision.description}
                </p>
              )}
            </div>

            <Button
              color="secondary"
              size="sm"
              onPress={() => onCompare(revision)}
              iconLeading={GitCompare}
            >
              {t('resources.assistants.revisions.compare')}
            </Button>
          </div>

          {/* Metadata */}
          <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              <span title={formattedDate}>{relativeTime}</span>
            </div>
            {authorName && (
              <div className="flex items-center gap-1.5">
                <User className="h-3.5 w-3.5" />
                <span>{authorName}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RevisionItem
